import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bell, CheckCheck, AlertTriangle, Info } from "lucide-react";

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const refreshCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count || 0);
    } catch { /* silent */ }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications/mine", { params: { limit: 20 } });
      setItems(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 60000); // poll every minute
    return () => clearInterval(t);
  }, []);

  useEffect(() => { if (open) loadItems(); }, [open]);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/mark-read`);
    setItems((xs) => xs.map((x) => x.id === id ? { ...x, is_unread: false } : x));
    setCount((c) => Math.max(0, c - 1));
  };

  const markAll = async () => {
    try {
      const { data } = await api.post("/notifications/mark-all-read");
      toast.success(`Marked ${data.updated || 0} as read`);
      setItems((xs) => xs.map((x) => ({ ...x, is_unread: false })));
      setCount(0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-2 rounded-md hover:bg-slate-100 transition-colors"
          data-testid="notification-bell"
          aria-label={`Notifications: ${count} unread`}
        >
          <Bell className="w-5 h-5 text-slate-700" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#7C1D3A] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0" data-testid="notification-dropdown">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div>
            <div className="font-heading font-semibold text-sm text-slate-900">Notifications</div>
            <div className="text-xs text-slate-500">{count} unread</div>
          </div>
          {count > 0 && (
            <Button size="sm" variant="ghost" onClick={markAll} className="text-xs" data-testid="mark-all-read-btn">
              <CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-xs text-slate-400">Loading...</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500">
              <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              You're all caught up 🎉
            </div>
          ) : items.map((n) => {
            const level = n.severity || (n.type?.includes("warranty") || n.type?.includes("amc") ? "warning" : "info");
            return (
              <div
                key={n.id}
                className={`px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${n.is_unread ? "bg-[#fcf0f3]/40" : ""}`}
                onClick={() => n.is_unread && markRead(n.id)}
                data-testid={`notification-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  {level === "warning"
                    ? <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    : <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{n.title || n.subject || n.type?.replace(/_/g, " ")}</div>
                    <div className="text-xs text-slate-600 line-clamp-2">{n.message || n.body || ""}</div>
                    <div className="text-[10px] text-slate-400 mt-1">{n.created_at?.slice(0, 16)?.replace("T", " ")}</div>
                  </div>
                  {n.is_unread && <span className="w-2 h-2 rounded-full bg-[#7C1D3A] shrink-0 mt-1.5" />}
                </div>
              </div>
            );
          })}
        </div>
        <NavLink
          to="/notifications"
          onClick={() => setOpen(false)}
          className="block px-4 py-2.5 text-center text-xs text-[#7C1D3A] hover:bg-slate-50 border-t border-slate-100 font-semibold"
        >
          View all →
        </NavLink>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
